// Plan 20-07 — META-08 web sticker overlay.
//
// Renders inbound emote stickers as 2-second floating overlays anchored to
// the relevant slot's name pill (opponent for received-from-opponent;
// self-slot for echoes / own-sent — though Plan 20-03 server does NOT
// echo to sender, the overlay supports the surface for completeness).
//
// State management lives in the parent (MatchScreen) — this component is
// a PURE render. The parent registers `onEmoteReceived(...)` via Plan
// 20-07 Task 1's ws-client, pushes ActiveSticker entries into a state
// array, and passes them as props. The component schedules a setTimeout
// for each sticker and fires `onExpire(key)` when its 2s lifetime elapses.
// Rapid-fire override is the parent's responsibility (prune-on-arrive).

import { type JSX, useEffect } from 'react'

export interface ActiveSticker {
  /** Unique key for React reconciliation + expiry routing. */
  readonly key: string
  /** Emote id — used to resolve the SVG asset URL. */
  readonly emoteId: string
  /** Sender's slot (0 or 1) — drives position anchoring. */
  readonly senderSlot: 0 | 1
  /** Wall-clock ms when the sticker entered the overlay (Date.now()). */
  readonly arrivedAt: number
}

export interface StickerOverlayProps {
  /** Local player's slot — determines which sticker is "from opponent". */
  readonly mySlot: 0 | 1
  /** Active stickers — most-recent-per-slot only (parent prunes). */
  readonly activeStickers: ReadonlyArray<ActiveSticker>
  /** Called with the sticker's key after its 2s lifetime elapses. */
  readonly onExpire: (key: string) => void
}

/** 2-second floating sticker lifetime. CONTEXT Area 4 Q4 spec. */
export const STICKER_LIFETIME_MS = 2000

export function StickerOverlay({
  mySlot,
  activeStickers,
  onExpire,
}: StickerOverlayProps): JSX.Element {
  useEffect(() => {
    const timers = activeStickers.map((sticker) => {
      const elapsed = Date.now() - sticker.arrivedAt
      const remaining = Math.max(0, STICKER_LIFETIME_MS - elapsed)
      return setTimeout(() => {
        onExpire(sticker.key)
      }, remaining)
    })
    return () => {
      for (const t of timers) clearTimeout(t)
    }
  }, [activeStickers, onExpire])

  return (
    <div
      className="sticker-overlay"
      data-screen-region="sticker-overlay"
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 60,
      }}
    >
      {activeStickers.map((sticker) => {
        const opponentSlot: 0 | 1 = mySlot === 0 ? 1 : 0
        const isFromOpponent = sticker.senderSlot === opponentSlot
        // Opponent's name pill lives at TOP; self's at BOTTOM. Anchor
        // sticker accordingly. CSS animation: float up + fade out.
        const positionStyle: React.CSSProperties = isFromOpponent
          ? { top: 48, right: 16 }
          : { bottom: 48, left: 16 }
        return (
          <div
            key={sticker.key}
            data-emote-id={sticker.emoteId}
            data-sender-slot={sticker.senderSlot}
            data-from-opponent={isFromOpponent ? 'true' : 'false'}
            role="img"
            aria-label={`Emote: ${sticker.emoteId}`}
            style={{
              position: 'absolute',
              ...positionStyle,
              width: 80,
              height: 80,
              transition: 'opacity 2000ms ease-out, transform 2000ms ease-out',
              opacity: 0,
              transform: 'translateY(-40px)',
              animation: 'sticker-fade 2000ms ease-out forwards',
            }}
          >
            <img
              src={`/emotes/${sticker.emoteId}.svg`}
              alt=""
              aria-hidden="true"
              width={80}
              height={80}
              style={{ display: 'block' }}
            />
          </div>
        )
      })}
    </div>
  )
}
